const IGAS_PREDICTIVE_SELECTORS = {
  form: '.igas-header-search',
  input: '.igas-header-search__input',
  panel: '[data-predictive-panel]',
  results: '[data-predictive-results]',
  loading: '[data-predictive-loading]',
  status: '[data-predictive-status]',
  viewAllLabel: '[data-predictive-view-all-label]',
  option: '.igas-predictive-search__option',
  navigable: '.igas-predictive-search__option, .igas-predictive-search__view-all',
};

const IGAS_SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

class IgasPredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.abortController = null;
    this.cache = new Map();
    this.debounceTimer = null;
    this.lastRenderedQuery = '';

    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.form = this.querySelector(IGAS_PREDICTIVE_SELECTORS.form);
    this.input = this.querySelector(IGAS_PREDICTIVE_SELECTORS.input);
    this.panel = this.querySelector(IGAS_PREDICTIVE_SELECTORS.panel);
    this.results = this.querySelector(IGAS_PREDICTIVE_SELECTORS.results);
    this.loading = this.querySelector(IGAS_PREDICTIVE_SELECTORS.loading);
    this.status = this.querySelector(IGAS_PREDICTIVE_SELECTORS.status);
    this.viewAllLabel = this.querySelector(IGAS_PREDICTIVE_SELECTORS.viewAllLabel);

    if (
      !(this.form instanceof HTMLFormElement) ||
      !(this.input instanceof HTMLInputElement) ||
      !(this.panel instanceof HTMLElement) ||
      !(this.results instanceof HTMLElement) ||
      !(this.loading instanceof HTMLElement)
    ) {
      return;
    }

    this.dataset.initialized = 'true';
    this.minQueryLength = Number.parseInt(this.dataset.minQueryLength || '2', 10);
    this.endpoint = this.createEndpoint();
    this.currencyFormatter = this.createCurrencyFormatter();

    this.input.addEventListener('input', this.handleInput);
    this.form.addEventListener('submit', this.handleSubmit);
    this.addEventListener('keydown', this.handleKeyDown);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('pointerdown', this.handleDocumentPointerDown);
  }

  disconnectedCallback() {
    window.clearTimeout(this.debounceTimer);
    this.abortController?.abort();

    this.input?.removeEventListener('input', this.handleInput);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener('keydown', this.handleKeyDown);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    delete this.dataset.initialized;
  }

  createEndpoint() {
    const route = this.dataset.predictiveSearchUrl || '/search/suggest';
    const url = new URL(route, window.location.origin);

    if (!url.pathname.endsWith('.json')) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
    }

    return url;
  }

  createCurrencyFormatter() {
    const configuredCurrency = (this.dataset.currency || 'EUR').toUpperCase();
    const currency = /^[A-Z]{3}$/.test(configuredCurrency) ? configuredCurrency : 'EUR';
    const locale = document.documentElement.lang || 'de-DE';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      });
    } catch (_error) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      });
    }
  }

  handleInput() {
    const query = this.input.value.trim();

    window.clearTimeout(this.debounceTimer);
    this.abortController?.abort();
    this.clearSelection();

    if (query.length < this.minQueryLength) {
      this.lastRenderedQuery = '';
      this.results.replaceChildren();
      this.closePanel();
      this.updateStatus('');
      return;
    }

    this.updateViewAllLabel(query);

    const cachedResults = this.cache.get(query.toLocaleLowerCase());
    if (cachedResults) {
      this.renderResults(cachedResults, query);
      return;
    }

    this.showLoading();
    this.debounceTimer = window.setTimeout(() => this.loadResults(query), 220);
  }

  handleSubmit(event) {
    if (!this.input.value.trim()) {
      event.preventDefault();
      this.input.focus();
      return;
    }

    this.closePanel();
  }

  handleDocumentPointerDown(event) {
    if (!this.contains(event.target)) {
      this.closePanel();
    }
  }

  handleFocusIn(event) {
    const option = event.target.closest?.(IGAS_PREDICTIVE_SELECTORS.option);
    if (option instanceof HTMLElement) {
      this.clearSelection();
      option.setAttribute('aria-selected', 'true');
      return;
    }

    if (
      event.target === this.input &&
      this.input.value.trim().length >= this.minQueryLength &&
      this.lastRenderedQuery === this.input.value.trim()
    ) {
      this.openPanel();
    }
  }

  handleFocusOut() {
    window.requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) {
        this.closePanel();
      }
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePanel();
      this.input.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const items = this.getNavigableItems();
    if (!items.length) return;

    event.preventDefault();
    this.openPanel();

    const currentIndex = items.indexOf(document.activeElement);
    let nextIndex;

    if (event.key === 'ArrowDown') {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    }

    items[nextIndex].focus();
  }

  async loadResults(query) {
    this.abortController = new AbortController();
    const url = new URL(this.endpoint.toString());

    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title');

    try {
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Predictive search request failed with status ${response.status}`);
      }

      const payload = await response.json();
      const searchResults = payload?.resources?.results || {};
      const normalizedResults = {
        collections: Array.isArray(searchResults.collections) ? searchResults.collections.slice(0, 3) : [],
        products: Array.isArray(searchResults.products) ? searchResults.products.slice(0, 6) : [],
      };

      if (this.input.value.trim() !== query) return;

      this.cache.set(query.toLocaleLowerCase(), normalizedResults);
      if (this.cache.size > 20) {
        this.cache.delete(this.cache.keys().next().value);
      }

      this.renderResults(normalizedResults, query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.input.value.trim() !== query) return;

      this.renderError(query);
    }
  }

  renderResults(searchResults, query) {
    const collections = searchResults.collections
      .map((collection) => this.normalizeResource(collection, '/collections/'))
      .filter(Boolean);
    const products = searchResults.products
      .map((product) => this.normalizeResource(product, '/products/'))
      .filter(Boolean);
    const fragment = document.createDocumentFragment();

    if (collections.length) {
      fragment.append(this.renderCollectionGroup(collections));
    }

    if (products.length) {
      fragment.append(this.renderProductGroup(products));
    }

    if (!collections.length && !products.length) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'igas-predictive-search__empty';
      emptyMessage.textContent = `Keine direkten Treffer für „${query}“. Über die vollständige Suche können weitere ähnliche Produkte gefunden werden.`;
      fragment.append(emptyMessage);
    }

    this.loading.hidden = true;
    this.results.setAttribute('aria-busy', 'false');
    this.results.replaceChildren(fragment);
    this.lastRenderedQuery = query;
    this.updateViewAllLabel(query);
    this.updateStatus(
      `${collections.length} Kategorien und ${products.length} Produkte für „${query}“ gefunden.`
    );
    this.openPanel();
  }

  renderError(query) {
    const errorMessage = document.createElement('p');
    errorMessage.className = 'igas-predictive-search__error';
    errorMessage.textContent = 'Die direkten Vorschläge konnten gerade nicht geladen werden. Die vollständige Suche funktioniert weiterhin.';

    this.loading.hidden = true;
    this.results.setAttribute('aria-busy', 'false');
    this.results.replaceChildren(errorMessage);
    this.lastRenderedQuery = query;
    this.updateViewAllLabel(query);
    this.updateStatus('Suchvorschläge konnten nicht geladen werden.');
    this.openPanel();
  }

  renderCollectionGroup(collections) {
    const group = this.createGroup('Passende Kategorien', 'collections');

    collections.forEach((collection) => {
      const listItem = document.createElement('li');
      const link = this.createOptionLink(collection, 'igas-predictive-search__collection');
      const icon = document.createElement('span');
      const label = document.createElement('span');

      icon.className = 'igas-predictive-search__collection-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.append(this.createCollectionIcon());
      label.textContent = collection.title;

      link.append(icon, label);
      listItem.append(link);
      group.list.append(listItem);
    });

    return group.element;
  }

  renderProductGroup(products) {
    const group = this.createGroup('Passende Produkte', 'products');

    products.forEach((product) => {
      const listItem = document.createElement('li');
      const link = this.createOptionLink(product, 'igas-predictive-search__product');
      const image = this.createProductImage(product);
      const copy = document.createElement('span');
      const title = document.createElement('span');
      const meta = document.createElement('span');
      const productType = String(product.type || '').trim();
      const price = this.formatPrice(product);
      const arrow = document.createElement('span');

      copy.className = 'igas-predictive-search__product-copy';
      title.className = 'igas-predictive-search__product-title';
      title.textContent = product.title;
      meta.className = 'igas-predictive-search__product-meta';

      if (productType) {
        const type = document.createElement('span');
        type.textContent = productType;
        meta.append(type);
      }

      if (price) {
        const priceElement = document.createElement('span');
        priceElement.className = 'igas-predictive-search__price';
        priceElement.textContent = price;
        meta.append(priceElement);
      }

      copy.append(title);
      if (meta.childElementCount) copy.append(meta);

      arrow.className = 'igas-predictive-search__product-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';

      link.append(image, copy, arrow);
      if (price) link.setAttribute('aria-label', `${product.title}, ${price}`);
      listItem.append(link);
      group.list.append(listItem);
    });

    return group.element;
  }

  createGroup(title, modifier) {
    const element = document.createElement('section');
    const heading = document.createElement('h3');
    const list = document.createElement('ul');

    element.className = 'igas-predictive-search__group';
    heading.className = 'igas-predictive-search__group-title';
    heading.textContent = title;
    list.className = `igas-predictive-search__list igas-predictive-search__list--${modifier}`;
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', title);

    element.append(heading, list);
    return { element, list };
  }

  createOptionLink(resource, className) {
    const link = document.createElement('a');
    link.className = `igas-predictive-search__option ${className}`;
    link.href = resource.url;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    return link;
  }

  createProductImage(product) {
    const wrapper = document.createElement('span');
    const imageUrl = this.getImageUrl(product);
    wrapper.className = 'igas-predictive-search__image';
    wrapper.setAttribute('aria-hidden', 'true');

    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = '';
      image.width = 124;
      image.height = 124;
      image.loading = 'lazy';
      image.decoding = 'async';
      wrapper.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'igas-predictive-search__image-placeholder';
      placeholder.textContent = '▦';
      wrapper.append(placeholder);
    }

    return wrapper;
  }

  createCollectionIcon() {
    const svg = document.createElementNS(IGAS_SVG_NAMESPACE, 'svg');
    const path = document.createElementNS(IGAS_SVG_NAMESPACE, 'path');

    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.8');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('d', 'M4 5.5h6.2l1.6 2H20v11H4z');
    svg.append(path);
    return svg;
  }

  normalizeResource(resource, expectedPath) {
    if (!resource || typeof resource.title !== 'string') return null;

    const title = resource.title.trim();
    const safeUrl = this.getSafeStoreUrl(resource.url, expectedPath);
    if (!title || !safeUrl) return null;

    return { ...resource, title, url: safeUrl };
  }

  getSafeStoreUrl(value, expectedPath) {
    if (typeof value !== 'string' || !value) return '';

    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes(expectedPath)) return '';
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return '';
    }
  }

  getImageUrl(product) {
    const value =
      product?.featured_image?.url ||
      product?.featured_image ||
      product?.image?.url ||
      product?.image ||
      '';

    if (typeof value !== 'string' || !value) return '';

    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' ? url.toString() : '';
    } catch (_error) {
      return '';
    }
  }

  formatPrice(product) {
    const rawPrice = product?.price_min ?? product?.price;
    const amount = typeof rawPrice === 'string' ? Number(rawPrice.replace(',', '.')) : Number(rawPrice);

    if (!Number.isFinite(amount)) return '';

    const minimum = Number(product?.price_min);
    const maximum = Number(product?.price_max);
    const varies = product?.price_varies === true || (Number.isFinite(minimum) && Number.isFinite(maximum) && minimum < maximum);

    return `${varies ? 'ab ' : ''}${this.currencyFormatter.format(amount)}`;
  }

  showLoading() {
    this.results.replaceChildren();
    this.results.setAttribute('aria-busy', 'true');
    this.loading.hidden = false;
    this.updateStatus('Suchvorschläge werden geladen.');
    this.openPanel();
  }

  openPanel() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.setAttribute('open', '');
  }

  closePanel() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('open');
    this.clearSelection();
  }

  clearSelection() {
    this.querySelectorAll(IGAS_PREDICTIVE_SELECTORS.option).forEach((option) => {
      option.setAttribute('aria-selected', 'false');
    });
  }

  getNavigableItems() {
    return Array.from(this.panel.querySelectorAll(IGAS_PREDICTIVE_SELECTORS.navigable)).filter(
      (item) => item instanceof HTMLElement && !item.hidden
    );
  }

  updateViewAllLabel(query) {
    if (!this.viewAllLabel) return;
    this.viewAllLabel.textContent = query
      ? `Alle Ergebnisse für „${query}“ anzeigen`
      : 'Alle Suchergebnisse anzeigen';
  }

  updateStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('igas-predictive-search')) {
  customElements.define('igas-predictive-search', IgasPredictiveSearch);
}
